import {
  Purchases,
  type Offerings as OfferingsWeb,
  type Offering as OfferingWeb,
  type Package as PackageWeb,
} from '@revenuecat/purchases-js';
import type {
  RevenueCatProvider,
  UnifiedOffering,
  UnifiedOfferings,
  UnifiedPackage,
} from './types';

const mapPackage = (pkg: PackageWeb): UnifiedPackage => ({
  identifier: pkg.identifier,
  priceString: pkg.webBillingProduct.price.formattedPrice,
  productIdentifier: pkg.webBillingProduct.identifier,
  title: pkg.webBillingProduct.title,
  description: pkg.webBillingProduct.description ?? '',
  original: pkg,
});

const mapOffering = (offering: OfferingWeb): UnifiedOffering => ({
  identifier: offering.identifier,
  serverDescription: offering.serverDescription,
  monthly: offering.monthly ? mapPackage(offering.monthly) : null,
  annual: offering.annual ? mapPackage(offering.annual) : null,
  availablePackages: offering.availablePackages.map(mapPackage),
});

const mapOfferings = (offerings: OfferingsWeb): UnifiedOfferings => ({
  current: offerings.current ? mapOffering(offerings.current) : null,
  all: Object.keys(offerings.all).reduce(
    (acc, key) => {
      acc[key] = mapOffering(offerings.all[key]);
      return acc;
    },
    {} as Record<string, UnifiedOffering>,
  ),
});

let purchasesInstance: ReturnType<typeof Purchases.configure> | undefined;

export const RevenuecatWebProvider: RevenueCatProvider = {
  // Initialize the revenuecat instance
  init: async ({ apiKey, appUserId }) => {
    if (!appUserId) {
      throw new Error('appUserId is required');
    }
    purchasesInstance = Purchases.configure({ apiKey, appUserId });
  },

  // Get the customer info
  getCustomerInfo: async () => {
    if (!purchasesInstance) {
      throw new Error('RevenueCat not initialized. Call init() first.');
    }
    return await purchasesInstance.getCustomerInfo();
  },

  // Get offerings
  getOfferings: async () => {
    if (!purchasesInstance) {
      throw new Error('RevenueCat not initialized. Call init() first.');
    }
    const offerings = await purchasesInstance.getOfferings();
    return mapOfferings(offerings);
  },

  // Present the paywall
  presentPaywall: async () => {
    if (!purchasesInstance) {
      throw new Error('RevenueCat not initialized. Call init() first.');
    }

    const paywallDialog = document.getElementById('paywall-dialog');

    if (!paywallDialog) {
      throw new Error('Paywall dialog not found');
    }

    // Create shadow root because paywall breaks tailwind styles
    paywallDialog.attachShadow({ mode: 'open' });
    const node = document.createElement('div');
    node.id = 'paywall-dialog-inner';
    node.style.minHeight = '200px';
    paywallDialog.shadowRoot?.appendChild(node);

    const target = paywallDialog.shadowRoot?.getElementById(
      'paywall-dialog-inner',
    );

    return purchasesInstance.presentPaywall({
      htmlTarget: target as HTMLElement,
    });
  },

  // Restore purchases not needed for web
  restorePurchases: async () => undefined,

  // Manage subscriptions
  manageSubscriptions: async () => {
    const customerInfo = await purchasesInstance?.getCustomerInfo();
    const url = customerInfo?.managementURL;

    console.log('customerInfo', customerInfo);
    console.log('url', url);

    if (!url) {
      throw new Error('No management URL found');
    }

    window.open(url, '_blank');
  },
};
